// src/app/helpers/ga4UserSegmentation.js
// Purpose: Map user profile data to GA4 user properties

import { setGA4Userproperties } from './ga4'

const isRbhsUser = (userProfile) => {
  if (!userProfile) return false
  if (userProfile.isRbhsStudent) return true
  const roles = Array.isArray(userProfile.roles) ? userProfile.roles : []
  return roles.some(
    (role) => typeof role === 'string' && role.toLowerCase().startsWith('rbhs'),
  )
}

// Campus names reported to GA4, by the location code in the user profile.
// The options are: Unknown, New Brunswick, Newark, Camden,
// RBHS New Brunswick, RBHS Newark and Off Campus.
const CAMPUS_BY_LOCATION_CODE = {
  1: 'New Brunswick',
  2: 'Newark',
  3: 'Camden',
  4: 'RBHS New Brunswick',
  5: 'RBHS Newark',
  6: 'Off Campus',
}

/**
 * Work out the campus from the location description text, for profiles
 * whose location code is missing or not in the table above.
 * Off Campus and the RBHS campuses are checked before the plain campuses,
 * because "RBHS Newark" also contains the word "Newark".
 */
const campusFromDescription = (locationDesc) => {
  const desc = String(locationDesc || '')

  if (desc.includes('Off Campus')) return 'Off Campus'
  if (desc.includes('RBHS')) {
    if (desc.includes('New Brunswick')) return 'RBHS New Brunswick'
    if (desc.includes('Newark') || desc.includes('NWK')) return 'RBHS Newark'
  }
  if (desc.includes('New Brunswick')) return 'New Brunswick'
  if (desc.includes('Newark') || desc.includes('NWK')) return 'Newark'
  if (desc.includes('Camden') || desc.includes('CMD')) return 'Camden'

  return null
}

/**
 * Extract campus from user profile
 * Uses the location code first, then the location description,
 * then the campus flags. Returns one of: Unknown, New Brunswick, Newark,
 * Camden, RBHS New Brunswick, RBHS Newark, Off Campus.
 */
const extractCampus = (userProfile) => {
  if (!userProfile) return 'Unknown'

  // Location code (the profile also carries its description, locationCdDesc)
  const code = String(userProfile.locationCd ?? '').trim()
  if (CAMPUS_BY_LOCATION_CODE[code]) return CAMPUS_BY_LOCATION_CODE[code]

  // Location description
  const fromDescription = campusFromDescription(userProfile.locationCdDesc)
  if (fromDescription) return fromDescription

  // Fallback to campus flags
  if (userProfile.isNB) return 'New Brunswick'
  if (userProfile.isNW) return 'Newark'
  if (userProfile.isCM) return 'Camden'

  return 'Unknown'
}

/**
 * Extract user type from user profile
 * Determines if user is Student, Faculty, Staff, Visitor, or combination
 */
const extractUserType = (userProfile) => {
  if (!userProfile) return 'Visitor'

  const types = []

  // Check employee status first (has priority)
  if (userProfile.isFaculty) {
    types.push('Faculty')
  } else if (userProfile.isStaff) {
    types.push('Staff')
  } else if (userProfile.isEmployee) {
    types.push('Employee')
  }

  // Check student status
  if (
    userProfile.isStudent ||
    userProfile.isRuStudent ||
    userProfile.isRbhsStudent
  ) {
    types.push('Student')
  }

  // Return combined type or default
  if (types.length > 0) {
    return types.join('+') // e.g., "Staff+Student"
  }

  return 'Visitor'
}

/**
 * Extract affiliation from user profile
 * Can be department or school
 */
const extractAffiliation = (userProfile) => {
  if (!userProfile) return 'Unknown'

  if (isRbhsUser(userProfile)) return 'RBHS'

  // Prefer department for employees
  if (userProfile.department) {
    return userProfile.department
  }

  // Use affiliation field
  if (userProfile.affiliation) {
    return userProfile.affiliation
  }

  return 'Unknown'
}

/**
 * Get user segmentation data to add to trackEvent calls
 * Returns object with campus, userType, affiliation
 */
export const getUserSegmentationData = (userProfile) => {
  if (!userProfile) {
    return {
      campus: 'Unknown',
      userType: 'Visitor',
      affiliation: 'Unknown',
    }
  }

  return {
    campus: extractCampus(userProfile),
    userType: extractUserType(userProfile),
    affiliation: extractAffiliation(userProfile),
  }
}

/**
 * Initialize GA4 user properties based on user profile
 * Call this after user login or when profile is loaded
 */
export const initializeGA4UserProperties = (userProfile) => {
  try {
    if (!userProfile) {
      console.log('No user profile available for GA4 initialization')
      return
    }

    const campus = extractCampus(userProfile)
    const userType = extractUserType(userProfile)
    const affiliation = extractAffiliation(userProfile)

    if (process.env.NODE_ENV === 'development') {
      console.log('GA4 User Properties:', {
        campus,
        userType,
        affiliation,
      })
    }

    // Send to GA4
    setGA4Userproperties({
      campus,
      userType,
      affiliation,
    })
  } catch (error) {
    console.error('Error initializing GA4 user properties:', error)
  }
}

/**
 * Update GA4 user properties if profile changes
 * (e.g., after switching roles/campuses)
 */
export const updateGA4UserProperties = (userProfile) => {
  initializeGA4UserProperties(userProfile)
}
